<!-- eslint-disable -->

<template>
  <div class="version-dropdown-root">
    <Dropdown
      ref="versionDropdown"
      v-model="version"
      :loading="(quote && !quotes) || fetchingQuotes || loadingVersions"
      :options="quoteOptions"
      optionLabel="label"
      optionValue="value"
      class="w-full"
      :dropdown="true"
      :disabled="(quote && !quotes) || fetchingQuotes || loadingVersions || isSaving"
      :optionDisabled="() => loadingVersions || updatingQuotes || isSaving"
      :emptyMessage="isNewQuote ? 'Create a quote first to save new versions.' : undefined"
    >
      <template #header="quotes">
        <Button
          severity="info"
          class="version-button-spacing"
          outlined
          text
          @click="loadingVersions || updatingQuotes ? null : addNewVersionWrapper()"
          :disabled="!quote?.id || loadingVersions || updatingQuotes || isSaving"
        >
          <span :class="loadingVersions || updatingQuotes 
            ? 'pi pi-spin pi-spinner version-button-text pr-2' 
            : 'pi pi-plus version-button-text pr-2'">
          </span>
          <span class="version-button-text">
            New Version
          </span>
          <span class="pl-2 text-gray-500" v-if="isQuoteWon">
            (Will reject current and previous versions)
          </span>
        </Button>
        <!-- <Button
          class="version-button"
          :label="'Apply Change Order'"
          severity="info"
          :icon="loadingVersions || updatingQuotes ? 'pi pi-spin pi-spinner' : 'pi pi-plus-circle'"
          outlined
          text
          @click="loadingVersions || updatingQuotes ? null : openDialog();"
          :disabled="loadingVersions || updatingQuotes || isSaving || !isQuoteWon"
        >
        </Button> -->
      </template>
    </Dropdown>
    <ApplyChangeOrderDialog
      :quote="quote"
      :openDialog="openDialog"
      :closeDialog="closeDialog"
      :isDialogVisible="isDialogVisible"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, toRaw, watch, onMounted, PropType, isProxy, isRef, Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  useGetQuotesQuery, 
  useInsertQuoteMutation, 
  useGetQuotesCountQuery, 
  useUpdateQuoteByPkMutation, 
  Quote_Status_Dataset_Enum, 
  Quote_Source_Dataset_Enum, 
  Quote_Line_Item_Source_Dataset_Enum,
  GetQuotesQueryVariables
} from "gql/api";
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import {
  exclude_fields,
  SKQuoteFields,
  SKQuoteLineItemFields,
  pick,
  statuses,
  translateBillableToQuoteLineItem,
  resortLocalQuoteLineItems,
  getArrayFromDeepNestedProxy,
  getQuoteVersionNavigationTarget,
  isQuoteEditorRoute,
  LineItemType
} from "@/utils/quoteHelpers";
import {
  type Scenarios,
  type Quotes,
  useDeleteSkQuoteMutation,
  useUpdateSkQuoteJobMutation,
  AllQuoteFieldsFragment as QuoteType,
  QuoteLineItemFieldsFragment as QuoteLineItemType,
} from "gql/api";
import { captureException } from "@sentry/vue";
import { getSpyGlassDate } from "@/utils/quoteHelpers";
import ApplyChangeOrderDialog from "@/components/quotes/ApplyChangeOrderDialog.vue";
import { useApplyChangeOrderStore } from '@/store/apply_change_order_store';
import type { SkBillable, Quote_Line_Items } from "@/assets/generated/api";
import { useToast } from "primevue/usetoast";

const { executeMutation: updateQuote } = useUpdateQuoteByPkMutation();
const { executeMutation: deleteSKQuote } = useDeleteSkQuoteMutation();
const { executeMutation: updateSKQuoteJob } = useUpdateSkQuoteJobMutation();

const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType>,
    default: null
  },
  saveQuote: {
    type: Function,
    required: true
  }
});

const { quote } = toRefs(props);

interface QuoteOptions {
  value: string,
  label: string
}

const version = ref<string>("");
const versionDropdown = ref<Dropdown | null>(null);
const quoteOptions = ref<Array<QuoteOptions>>([]);
const quoteId = computed(() => route.params?.quoteId );
const isNewQuote = computed(() => !quoteId.value );
const loadingVersions = ref<boolean>(false);
const updatingQuotes = ref<boolean>(false);
const numQuotesUpdated = ref<number>(0);
const isSaving = ref<boolean>(false);
const isDialogVisible = ref<boolean>(false);
const isQuoteWon = computed(() => quote?.value?.status === statuses.won)
const applyChangeOrderStore = useApplyChangeOrderStore();
const toast = useToast();

const getVersionLabel = (q: QuoteType) =>
  `Version ${q.version_number}: ${q.name || '(pending)'} | ${getSpyGlassDate(q.created_at)}`;

const versionLabel = computed(() => {
  const _q = toRaw(quote.value);
  return getVersionLabel(_q);
})

const router = useRouter();
const route = useRoute();

watch(version, () => {
  // SK-5222: only navigate for a real version change while the quote editor
  // is still the active route. Leaving the editor clears the route's quoteId,
  // which used to set the version to "new" and push the user to Create Quote.
  const targetQuoteId = getQuoteVersionNavigationTarget(
    version.value,
    route.params.quoteId,
    isQuoteEditorRoute(route)
  );
  if (targetQuoteId) {
    router.push({
      name: "EditQuote",
      params: {
        quoteId: targetQuoteId
      }
    });
  }
});

const { executeQuery: getQuotesCount } = useGetQuotesCountQuery({
  requestPolicy: "network-only",
  pause: true,
  variables: {}
});

const {
  data: quotes,
  executeQuery: fetchQuotes,
  fetching: fetchingQuotes
} = useGetQuotesQuery({
  requestPolicy: "network-only",
  pause: isNewQuote,
  variables: computed(() => {
    /* Race condition note: route.params.quoteId may come back first
    before quote.value.id depending on what the user is doing.
    This seems to happen after save as new, and it may happen in
    other scenarios.
    Either way, we need a quote ID unless we want to poll for all
    quotes and have the UI hang in prod (see limit parameter below). 
    */
    let quote_id = quote?.value?.id || quoteId.value;

    if (quote?.value?.parent?.id && quote?.value?.version_number && quote?.value?.version_number > 1) {
      quote_id = quote.value.parent?.id;
    }

    return {
      limit: !quote_id ? 0 : null,
      order_by: { version_number: "asc" },
      where: {
        _or: [{ parent_id: { _eq: quote_id } }, { id: { _eq: quote_id } }],
        deleted_at: {
          _is_null: true
        }
      }
    };
  }) as GetQuotesQueryVariables
});


const loadVersions = () => {
  if (isNewQuote.value) {
    version.value = "new";
    quoteOptions.value = [];
    return Promise.resolve();
  }

  return Promise.resolve()
    .then(() => fetchQuotes())
    .then(resp => {
      // remove rejected?
      quoteOptions.value = (resp?.data?.value?.quotes || [])
        .filter((q): q is QuoteType => q !== null)
        .map(q => {
          return { value: q.id, label: getVersionLabel(q) };
        });

      if (route.params.quoteId === "new") {
        version.value = "new";
        return;
      }

      version.value = Array.isArray(route.params.quoteId) 
        ? route.params.quoteId[0]
        : route.params.quoteId;
    });
}

watch([quoteId, () => quote?.value?.id], () => {
  // Ignore the route change caused by leaving the quote editor.
  if (!isQuoteEditorRoute(route)) {
    return;
  }
  // TODO: fetchingQuotes does not always set to true when fetching,
  // so I created a new boolean to handle this edge case. This is needed
  // for UI loading feedback.
  loadingVersions.value = true;
  loadVersions().finally(() => {
    updatingQuotes.value = false;
    numQuotesUpdated.value = 0;
    loadingVersions.value = false;
  });
})

onMounted(() => {
  loadVersions();
})

/**
 * Allow users to (immediately) update the version label if they copy the quote.
 * We can do this by watching the quote name value only.
 * 
 * If requirements to UI/UX changes (such as updating always), just force
 * invoke getVersionLabel on every save (and remove isCopiedQuote requirement if needed)
 */
watch(() => quote?.value?.name, (newQuoteName, oldQuoteName) => {
  
  const currentQuoteOption = quoteOptions.value.find((_quote) => _quote.value === quote?.value?.id);
  const isCopiedQuote = quote?.value?.source === Quote_Source_Dataset_Enum.CopyQuote;
  const isQuoteNameReset = oldQuoteName === null || oldQuoteName == ''; // Resets to null or ''
  const isQuoteNameNew = newQuoteName !== null || newQuoteName != '';
  const validToUpdateVersionLabel = currentQuoteOption && isCopiedQuote && isQuoteNameReset && isQuoteNameNew;
  if (validToUpdateVersionLabel) {
    currentQuoteOption.label = getVersionLabel(toRaw(quote.value));
  }
})

const { executeMutation: insertQuote } = useInsertQuoteMutation();

const serializeQuoteLineItem = (item: QuoteLineItemType) => {
  exclude_fields.forEach(field => {
    const fieldVal = item[field];
    delete item[field];
    if (fieldVal && fieldVal?.id) {
      (item as any)[`spyglass_${field}`] = fieldVal.id; // TODO: remove as any and resolve
    }
  });
  return item;
};

/**
 * This function removes IDs on quote line items such that, when we add a new
 * version and/or apply a change order, the copied over quote line items have
 * blank IDs for Hasura to work with.
 *
 * Other needed mechanics will apply, such as setting the source to "new_version".
 *
 * @param {any} item - the quote line item
 * @returns {item} - a quote line item with deleted IDs and other characteristics
 */
const removeId = (quote: QuoteType | Ref<QuoteType>) => {
  const __quote = isRef(quote) ? quote?.value : quote;

  // TODO: coming back to this bandaid fix later
  __quote.quote_line_items = isProxy(__quote.quote_line_items)
    ? getArrayFromDeepNestedProxy(__quote.quote_line_items)
    : __quote.quote_line_items;

  let _quote = structuredClone(toRaw(__quote));
  delete _quote.id;
  delete _quote.created_at;
  delete _quote.updated_at;
  delete (_quote as any).__typename; // TODO: optional typename?
  return _quote;
};

const removeIdFromQli = (qli: QuoteLineItemType | Ref<QuoteLineItemType>) => {
  const __qli = isRef(qli) ? qli?.value : qli;

  let _qli = structuredClone(toRaw(__qli));
  if (_qli?.line_item_type) {
    _qli = serializeQuoteLineItem(_qli);
    if (_qli.source !== Quote_Line_Item_Source_Dataset_Enum.ChangeOrder) {
      _qli.source = Quote_Line_Item_Source_Dataset_Enum.NewVersion;
    }
  }

  delete _qli.id;
  delete _qli.quote_id;
  delete _qli.created_at;
  delete _qli.updated_at;
  delete (_qli as any).__typename; // TODO: optional typename?
  return _qli;
}

function appendVersion(str: string, version: string = "V2"): string {
  const firstDashIndex = str.indexOf("-");

  // Needed for versions with more than one digit.
  // If there’s a dash, slice the string up to (but not including) that dash
  // Otherwise, just keep the entire string as our base
  const base = firstDashIndex !== -1 ? str.substring(0, firstDashIndex) : str;

  return `${base}-${version}`;
}

const getButtonLabel = computed(() => {
  if (loadingVersions.value) {
    return 'Adding version, please wait...'
  }
  if (updatingQuotes.value) {
    return `Updating previous quotes, please wait...\n${numQuotesUpdated.value}/${quoteOptions.value.length}`
  }

  return 'New Version'
})

const addNewVersion: () => void = () => {
  if (!quote.value?.id) return;
  const _quote: any = removeId(quote); // TODO: we're using this as Quote_Insert_Input, not sure what to do here yet
  _quote.parent_id = _quote?.parent?.id || quote?.value?.id;
  _quote.version_number = (quoteOptions.value.length || 1) + 1;
  _quote.source = Quote_Source_Dataset_Enum.NewVersion;
  _quote.status = Quote_Status_Dataset_Enum.Draft;

  delete _quote.quote_scenario;
  delete _quote.deleted_at;
  delete _quote.parent;
  delete _quote.user;

  /*
  * (1/3) We should first check if this add new version request is from a change order.
  * If it is, translate any found items to QLIs and store it on the quote object.
  * This enables eventual seamless ID removal before we post to Hasura.
  */
  if (applyChangeOrderStore.isApplyingChangeOrder) {
    /*
    * Note: doing it this way has the benefit of calculating sub_orders for the
    * complex scenario where the change order comes back with line item type variety.
    * Change order CAN and WILL come back with > 1 items of > 1 line item types.
    *
    * This is oppositional to the default workflow where users are only able to add
    * ONE line item type at time. Change orders can add MULTIPLE line item types at
    * at a time. It's important that we return the correct suborders.
    */
    const billables: SkBillable[] = applyChangeOrderStore.billablesNotOnQuote;
    billables.forEach((billable: SkBillable) => {
      const line_item_type: LineItemType = applyChangeOrderStore.getLineItemTypeByBillableID(billable.id);
      const quote_line_item: QuoteLineItemType = translateBillableToQuoteLineItem(billable, line_item_type, quote?.value);
      quote?.value?.quote_line_items.push(quote_line_item);
    })

    // After sub_order calculation, the list needs to be resorted for proper UI display.
    quote.value.quote_line_items = resortLocalQuoteLineItems(quote?.value?.quote_line_items)
  }

  /*
   * (2/3) Remove all IDs from quote line items so that Hasura can create new ones
   * in place. Will error otherwise. Applicable for all scenarios.
   */
  const _quote_line_items: QuoteLineItemType[] =
    (quote?.value?.quote_line_items as Quote_Line_Items[] || []).map(
      removeIdFromQli
    );

  // (3/3) Finally, store the processed _quote_line_items on the _quote object
  _quote.quote_line_items = { data: _quote_line_items };
  _quote.sk_proposal_number = appendVersion(_quote.sk_proposal_number, `V${_quote.version_number}`);

  //const deleteQuotePromise = Promise.resolve()
  //  .then(() => {
  //    return deleteSKQuote({ object: { id: 1 } });
  //  })
  //  .then((del_resp) => {
  //    if (!del_resp) {
  //      console.error("Error deleting sk quote.");
  //    }
  //  });


  // if new version is selected on a won quote
  if (quote?.value?.status === statuses.won) {
    const skQuote = Object.assign({
      job: quote?.value.sk_job_id,
      spyglass_id: quote?.value.id,
      rules: quote?.value.quote_line_items.map(qli => {
        return pick(toRaw(qli), SKQuoteLineItemFields);
      })
    },
      pick(toRaw(quote?.value), SKQuoteFields)
    )

    const updateQuoteJobPromise = Promise.resolve()
      .then(() => {
        return updateSKQuoteJob({ object: skQuote });
      })
      .then((del_resp) => {
        if (!del_resp) {
          console.error("Error updating sk quote job.");
        }
      });

    const updateQuoteStatusPromises = quoteOptions.value.map((q) => {
      return updateQuote({
        id: q.value,
        object: { status: Quote_Status_Dataset_Enum.Rejected },
        create_line_items: [],
        update_line_items: []
      })
    })

    updatingQuotes.value = true;
    updateQuoteStatusPromises.forEach((promise) => {
      promise.finally(() => {
        // Regardless of outcome, increment for user
        numQuotesUpdated.value += 1;
      })
    })

    Promise.all(updateQuoteStatusPromises)
      .catch((err) => {
        captureException(
          new Error("Error Saving Old Versions As Rejected"),
          {
            extra: {
              quote_id: quote?.value?.id,
              message: err.message
            }
          }
        );
      })
  }

  return insertQuote({ object: _quote })
    .then(resp => {
      const _quoteId = resp?.data?.insert_quotes_one?.id;
      if (_quoteId) {
        // TODO: might want to push based on current tab open
        return router.push({
          name: applyChangeOrderStore.isApplyingChangeOrder
            ? "EditQuoteQuote"
            : "EditQuote",
          params: {
            quoteId: _quoteId
          }
        });
      }
      captureException(
        new Error("Error Creating Quote From Version Dropdown"),
        {
          extra: {
            quote_id: quote?.value?.id,
            response: resp.data
          }
        }
      );
      throw new Error("Error Creating Quote From Version Dropdown");
    })
    .catch(err => {
      isSaving.value = false;
      console.error(err);
      alert("Error creating quote! Contact support.");
    });
};

const addNewVersionWrapper = async () => {
  if (isSaving.value || !quote.value?.id) {
    return;
  }
  isSaving.value = true;
  try {
    await props.saveQuote();
    await addNewVersion();
    if (applyChangeOrderStore.isApplyingChangeOrder) {
      toast.add({
        severity: "success",
        summary: "Change Order successfully applied",
        detail: "Automatically redirected to your new change order.",
        life: 7000
      });
    }
  } finally {
    isSaving.value = false;
    applyChangeOrderStore.isApplyingChangeOrder = false;
  }
};

const openDialog = () => {
  isDialogVisible.value = true;
  if (versionDropdown.value) {
    versionDropdown.value.hide();
  }
};

const closeDialog = () => {
  isDialogVisible.value = false;
  if (applyChangeOrderStore.isApplyingChangeOrder)  {
    addNewVersionWrapper();
  }
};
</script>

<style scoped lang="scss">
.version-button-spacing {
  padding-bottom: .5rem;
  padding-top: .5rem;
  padding-left: .5rem;
  padding-right: .5rem;
  width: 100%;
}
.version-button-text {
  color: $highlightFocusBg;
  text-align: left;
}
.version-button {
  @extend .version-button-spacing;
  @extend .version-button-text;
}
.version-button:hover, 
.version-button-spacing:hover {
  box-shadow: inset 0 0 0 0.1rem var(--surface-400);
}
</style>
